import React, { useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import DashboardHeader from "../../components/Other/DashboardHeader";
import LeaderboardSecton from "../../components/Other/LeaderboardSecton";
import { LeagueIdentityBlock } from "./league/LeagueShell";
import LeagueResultStatusLine from "./landing/LeagueResultStatusLine";
import { getLeagueStatus } from "./landing/leagueStatus";
import { getGameFromSlug } from "../../api/user";
import BackArrowButton from "./landing/BackArrowButton";

// The league leaderboard, standing on its own inside the main QUSHi site.
//
// Client, League Play Dev Request, "Leaderboard Routing":
//
//   "View Results" CTA from any completed card must open the final leaderboard
//   directly - not take people into the league home or the league shell.
//   "View Leaderboard" CTA from a live public league must open the live
//   leaderboard directly. - outside the league shell
//   These leaderboard views must remain inside the main QUSHi site and must not
//   open the league shell.
//   Reuse the existing leaderboard component with live and completed states. Do
//   not create duplicated leaderboard components.
//   The back arrow must return the user to the screen they came from.
//
// So this file is deliberately thin. The board is LeaderboardSecton — the same
// component /leaderboard and /<slug>/leaderboard render — and everything this
// page adds is the three things the client listed: a back arrow, the league
// identity, and the status line beneath it. There is no second board, no second
// podium and no second row component anywhere in here.
//
// What makes it "outside the league shell" is the ADDRESS, not a flag. The
// league shell switches on at any URL leagueSlugFromPath resolves, which is
// /contest/<slug>/… and the twelve /<slug>/<screen> routes. /leaderboards/<slug>
// is neither, so the app keeps its own left rail, its own bottom bar and its own
// header — a public visitor who never joined this league is never shown a league
// nav they cannot use. See utils/leaguePath.js.
//
// GameLeaderboard (/<slug>/leaderboard) is untouched and still serves the
// in-league tab a member reaches from the league's own rail.

const LeagueLeaderboardStandalone = () => {
  const [sidebarToggle] = useOutletContext() || [];
  const { slug } = useParams();
  const [game, setGame] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getGameFromSlug({ slug })
      .then((res) => {
        if (cancelled) return;
        // A miss answers 200 with success:false, so the body decides.
        const data = res?.data?.success === false ? null : res?.data?.data;
        setGame(data?.id ? data : null);
        setReady(true);
      })
      .catch((e) => {
        console.error("Standalone leaderboard load failed:", e);
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const completed =
    game?.status === "completed" || getLeagueStatus(game)?.tone === "ended";

  // Which of the two trailing links the status line offers:
  //
  //   member      "League Home ›"   — the way back inside
  //   non-member  "Game details ›"  — the preview-only details screen
  //
  // isMember is the league's own answer (getGameFromSlug), not a guess from the
  // URL, so a visitor previewing a public league is correctly never offered the
  // way into a league they are not in.
  const isMember = !!game?.isMember;

  const header = (
    <div className="mb-5 md:mb-6">
      {/* DESKTOP ONLY now. Client: "Put the standard back arrow in the avatar's
          place, top-left in the header" — on a phone that IS the app's fixed
          header bar, and DashboardHeader draws it there (see
          isStandaloneLeaderboardPath). Leaving this one visible as well would
          put two back arrows on the same screen, one above the other.

          Desktop keeps it here because that is where the client put the arrow at
          this width: "Desktop: use the content width to line up the arrow (left
          margin), share (right margin)." This row IS the content column's left
          edge. No share icon beside it — "leaderboards are not shareable yet." */}
      <BackArrowButton className="hidden md:flex" />
      {/* The identical identity block Portfolio, Watchlist and the in-league
          Leaderboard render (LeagueShell's `compact` mode) — client Rev 5 asked
          for that block to be one definition, and this screen is not an
          exception to it. */}
      <LeagueIdentityBlock game={game} />
      {/* "Add the status line directly beneath the league title." */}
      <LeagueResultStatusLine
        game={game}
        completed={completed}
        isMember={isMember}
        className="mt-1.5"
      />
    </div>
  );

  return (
    <>
      <DashboardHeader toggle={sidebarToggle} />
      {ready && game && (
        <LeaderboardSecton
          gameId={game.id}
          slug={slug}
          game={game}
          standalone
          header={header}
        />
      )}
    </>
  );
};

export default LeagueLeaderboardStandalone;
